/**
 * The unpublish confirmation — phase 2 §4.4.
 *
 * Publishing's dialog leads with who gains access. This one leads with who
 * loses it, because that is the whole content of the action: the document is
 * unchanged, its version is unchanged, and the only thing that moves is who can
 * find it.
 *
 * The second thing it leads with is the refusal. §4.4 makes unpublish
 * unavailable once a client has commented or acknowledged, and the *right*
 * moment to say so is here rather than in a 409 the operator meets after
 * deciding — so the engagement block renders first when it is engaged, names
 * the people, and points at archive. The server still refuses; this is not the
 * enforcement, and moving the rule here would be the reimplementation §4.7
 * warns about. It is the difference between a dialog that explains and a dialog
 * that lies.
 *
 * Every number below is the server's. The plan is computed by running the move
 * inside a transaction and rolling it back (`documents/move.ts`), which is the
 * same construction ADR 0004 requires of the publish preview and for the same
 * reason.
 */

import type { ReactNode } from 'react';
import { useState } from 'react';
import { useUnpublishDocument, useUnpublishPreview } from '../../api/queries.ts';
import type {
  AudienceMember,
  DocumentView,
  EngagementRecord,
  UnpublishPlan,
  UnpublishResponse,
} from '../../api/types.ts';
import { Badge, RoleBadge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState, Loading } from '../../components/ui/feedback.tsx';
import { TextField } from '../../components/ui/field.tsx';
import { Mono } from '../../components/ui/layout.tsx';
import { SpaceKindBadge } from './vocabulary.tsx';

function Section({
  title,
  tone = 'plain',
  children,
}: {
  title: string;
  tone?: 'plain' | 'warning';
  children: ReactNode;
}) {
  return (
    <section
      className={
        tone === 'warning'
          ? 'rounded-lg border border-warning bg-warning-primary px-4 py-3'
          : 'rounded-lg border border-secondary px-4 py-3'
      }
    >
      <h3 className="text-xs font-semibold tracking-wide text-tertiary uppercase">{title}</h3>
      <div className="mt-2 flex flex-col gap-2 text-sm text-secondary">{children}</div>
    </section>
  );
}

function PersonLine({ person }: { person: AudienceMember }) {
  return (
    <li className="flex flex-wrap items-center gap-2">
      <span className="font-medium text-primary">{person.name}</span>
      <span className="text-xs text-tertiary">{person.email}</span>
      <RoleBadge role={person.role} />
    </li>
  );
}

function EngagementLine({ record }: { record: EngagementRecord }) {
  return (
    <li className="flex flex-wrap items-center gap-2">
      <span className="font-medium text-primary">{record.personName}</span>
      <Badge tone="outline">{record.kind}</Badge>
      <span className="text-xs text-tertiary">{record.nodeTitle}</span>
      <span className="text-xs text-tertiary">{new Date(record.at).toLocaleDateString()}</span>
    </li>
  );
}

/**
 * §4.4's refusal, rendered as the reason it exists rather than as an error.
 *
 * Naming the people is the point: "the client has engaged" is a state somebody
 * has to go and check, and "Priya commented on Homepage Concepts on the 4th" is
 * a sentence that ends the question. `nodeTitle` earns its place here — the
 * comment may be on a child document travelling with this one, which is the
 * case an operator is least likely to have in mind.
 */
function Engagement({ plan }: { plan: UnpublishPlan }) {
  const { engagement } = plan;
  const records = [...engagement.comments, ...engagement.acknowledgments];

  return (
    <Section title="Client engagement" tone={engagement.engaged ? 'warning' : 'plain'}>
      <p className={engagement.engaged ? 'font-semibold text-primary' : undefined}>
        {engagement.explanation.replaceAll('**', '')}
      </p>
      {records.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {records.map((record) => (
            <EngagementLine
              key={`${record.kind}-${record.nodeId}-${record.personId}`}
              record={record}
            />
          ))}
        </ul>
      ) : null}
    </Section>
  );
}

/** Who stops being able to read this — the reason the dialog exists. */
function Audience({ plan }: { plan: UnpublishPlan }) {
  const { loses, after } = plan.audience;
  const [showAll, setShowAll] = useState(false);

  return (
    <Section title="Audience" tone={loses.length > 0 ? 'warning' : 'plain'}>
      {loses.length === 0 ? (
        <p>
          Nobody loses access — the {after.length} {after.length === 1 ? 'person' : 'people'} who
          can read this could read it before the move too. Worth a second look: withdrawing
          something no client could reach is usually not the action that was wanted.
        </p>
      ) : (
        <div>
          <p className="font-semibold text-primary">
            {loses.length} {loses.length === 1 ? 'person loses' : 'people lose'} access
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {loses.map((person) => (
              <PersonLine key={person.personId} person={person} />
            ))}
          </ul>
        </div>
      )}

      <div>
        <Button size="sm" variant="tertiary" onPress={() => setShowAll((open) => !open)}>
          {showAll ? 'Hide' : 'Show'} everyone who can read it afterwards ({after.length})
        </Button>
        {showAll ? (
          <ul className="mt-2 flex flex-col gap-1">
            {after.map((person) => (
              <PersonLine key={person.personId} person={person} />
            ))}
          </ul>
        ) : null}
      </div>
    </Section>
  );
}

function Destination({ plan }: { plan: UnpublishPlan }) {
  const travelling = plan.moving.filter((node) => node.nodeId !== plan.nodeId);

  return (
    <Section title="Destination">
      <p className="flex flex-wrap items-center gap-2">
        <span>{plan.from.spaceName}</span>
        <SpaceKindBadge kind={plan.from.spaceKind} />
        <span aria-hidden="true">→</span>
        <span className="font-semibold text-primary">{plan.to.spaceName}</span>
        <SpaceKindBadge kind={plan.to.spaceKind} />
      </p>
      <p>
        It lands at the root of {plan.to.spaceName}, keeping its id — the URL does not change, and a
        link already handed out still resolves for anyone who can still reach it.
      </p>
      <p>
        The document itself is untouched: nothing is snapshotted, the published version stays the
        version readers resolve to, and no acknowledgment cycle reopens. What changes is who can
        find it.
      </p>
      {travelling.length > 0 ? (
        <div>
          <p>
            {travelling.length} child {travelling.length === 1 ? 'node travels' : 'nodes travel'}{' '}
            with it — a descendant cannot be left behind, because a child node must live in its
            parent&apos;s space.
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {travelling.map((node) => (
              <li key={node.nodeId} className="flex flex-wrap items-center gap-2">
                <span className="text-primary">{node.title}</span>
                <Badge tone="outline">{node.type}</Badge>
                {node.deleted ? <Badge tone="gray">deleted</Badge> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {plan.nodeOverrides.length > 0 ? (
        <p>
          {plan.nodeOverrides.length} node-level{' '}
          {plan.nodeOverrides.length === 1 ? 'grant is' : 'grants are'} re-evaluated against the new
          ancestry. The grants do not move; their meaning does.
        </p>
      ) : null}
    </Section>
  );
}

/**
 * §4.4's one escape hatch, and it is deliberately not a general-purpose
 * override: it exists because a document published into the wrong client's
 * space is a leak that archive cannot fix, since archiving leaves the node
 * where it is and a direct link still resolves.
 */
function ForceOverride({
  force,
  setForce,
  reason,
  setReason,
}: {
  force: boolean;
  setForce: (value: boolean) => void;
  reason: string;
  setReason: (value: string) => void;
}) {
  if (!force) {
    return (
      <Button size="sm" variant="tertiary" onPress={() => setForce(true)}>
        Withdraw it anyway, as an administrative override
      </Button>
    );
  }

  return (
    <Alert tone="warning" title="This is an administrative override">
      <div className="mt-2 flex flex-col gap-3">
        <p>
          The case this exists for is a document published into the wrong client&apos;s space.
          Authorized against <Mono>admin.override</Mono> and recorded as{' '}
          <Mono>admin.force_unpublish</Mono> with this reason attached — never as an ordinary
          withdrawal.
        </p>
        <TextField
          label="Reason"
          value={reason}
          onChange={setReason}
          multiline
          rows={2}
          isRequired
        />
        <div>
          <Button size="sm" variant="tertiary" onPress={() => setForce(false)}>
            Cancel the override
          </Button>
        </div>
      </div>
    </Alert>
  );
}

export interface UnpublishDialogProps {
  document: DocumentView;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onUnpublished?: (result: UnpublishResponse) => void;
}

export function UnpublishDialog({
  document,
  isOpen,
  onOpenChange,
  onUnpublished,
}: UnpublishDialogProps) {
  return (
    <Dialog
      title="Unpublish"
      description={document.title}
      size="lg"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      {(close) => (
        <UnpublishConfirmation
          document={document}
          onClose={close}
          {...(onUnpublished ? { onUnpublished } : {})}
        />
      )}
    </Dialog>
  );
}

function UnpublishConfirmation({
  document,
  onClose,
  onUnpublished,
}: {
  document: DocumentView;
  onClose: () => void;
  onUnpublished?: (result: UnpublishResponse) => void;
}) {
  const preview = useUnpublishPreview(document.nodeId);
  const withdraw = useUnpublishDocument(document.nodeId);
  const [force, setForce] = useState(false);
  const [reason, setReason] = useState('');

  const plan = preview.data?.confirmation;
  const reasonMissing = force && reason.trim() === '';

  return (
    <>
      <DialogBody>
        {preview.isPending ? <Loading label="Working out who this takes it from…" /> : null}
        {preview.isError ? <ErrorState error={preview.error} /> : null}

        {plan ? (
          <>
            {/* First, because it decides whether the rest of this dialog is a
                decision or an explanation. */}
            <Engagement plan={plan} />
            <Destination plan={plan} />
            <Audience plan={plan} />
            {plan.engagement.engaged ? (
              <ForceOverride
                force={force}
                setForce={setForce}
                reason={reason}
                setReason={setReason}
              />
            ) : null}
          </>
        ) : null}

        {withdraw.isError ? <ErrorState error={withdraw.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={withdraw.isPending}
          // Engaged and not overriding means the server will refuse, and a
          // button that is live and then 409s teaches people the dialog is
          // lying to them. The server is still the enforcement.
          isDisabled={!plan || reasonMissing || (plan.engagement.engaged && !force)}
          onPress={() => {
            if (!plan) return;
            withdraw.mutate(
              { confirmed: true, ...(force ? { force: true, reason: reason.trim() } : {}) },
              {
                onSuccess: (result) => {
                  onUnpublished?.(result);
                  onClose();
                },
              },
            );
          }}
        >
          {plan ? `Move back to ${plan.to.spaceName}` : 'Unpublish'}
        </Button>
      </DialogFooter>
    </>
  );
}

/** What just happened, for the page to render after the dialog closes. */
export function UnpublishedNotice({ result }: { result: UnpublishResponse }) {
  const { unpublished } = result;
  return (
    <Alert tone="success" title={`Moved back into ${unpublished.to.spaceName}`}>
      <p>
        {unpublished.audience.loses.length} lost access and {unpublished.moved.length}{' '}
        {unpublished.moved.length === 1 ? 'node' : 'nodes'} moved. The published version is
        unchanged — this withdrew the document from the client, it did not revise it.
      </p>
    </Alert>
  );
}
